import React from 'react'
import { TooltipProps, ResponsiveContainer } from 'recharts'
import { Card, H4 } from '@blueprintjs/core'
import { Column } from '../ReportPage'
import { Flex, Button, IconButton, Dialog } from '@ui'
import PrettyNumber from '@ui/PrettyNumber'
import { IGroup } from '../types'
import Bar from '@ui/charts/Bar'
import ChartTooltip from '@ui/charts/ChartTooltip'

const MAX_BARS = 6

interface IProps {
  chart: IGroup
  layout: 'vertical' | 'horizontal'
  width?: number
  height?: number
  config: any
  onClick?: (a: any) => void
  colors?: any | undefined
}

export default function BarWrapper({
  chart,
  layout,
  width = 200,
  height = 200,
  config,
  colors,
  onClick,
}: IProps) {
  const [showModal, setShowModal] = React.useState<boolean>(false)
  const windowWidth = window.innerWidth
  const data = formatChartData({
    columns: chart.columns,
    values: chart.values,
    horizontal: layout === 'horizontal',
    columnTypes: chart.columnTypes,
    conf: config,
    colors: colors,
  })

  const truncated = layout === 'vertical' && data.length > MAX_BARS
  const DATA = layout === 'vertical' ? data.slice(0, MAX_BARS) : data

  return (
    <Column>
      <Card style={{ height: '100%', width: '100%', position: 'relative' }}>
        <H4>{chart.name}</H4>
        {truncated && (
          <Flex
            align="center"
            style={{
              position: 'absolute',
              top: 45,
              left: 20,
              zIndex: 10,
            }}
          >
            <span style={{ marginRight: 8 }}>
              Top {MAX_BARS}/{data.length} shown
            </span>
            <Button
              small
              minimal
              blue
              text="Show All"
              style={{ height: 32, paddingTop: 5, paddingBottom: 5 }}
              onClick={() => setShowModal(true)}
            />
          </Flex>
        )}
        <ResponsiveContainer height={height} width="100%">
          <Bar
            pretty
            data={DATA}
            height={height}
            width={width}
            xdata={{ label: config.xLabel, key: config.x }}
            ydata={{ label: config.yLabel, key: config.y }}
            percent={config.unit === 'percent'}
            layout={layout}
            colors={colors}
            tooltip={(props) => <CustomTooltip config={config} {...props} />}
            margin={{ top: 30, right: 10, left: 0, bottom: 5 }}
            onClick={onClick}
          />
        </ResponsiveContainer>
        {showModal && (
          <Dialog
            isOpen={true}
            isCloseButtonShown={false}
            usePortal
            style={{
              width: 900,
              height: data.length * 40 + 100,
              position: 'relative',
            }}
            title={chart.name}
            toolbar={
              <div className="dialog-buttons">
                <IconButton
                  minimal
                  iconName="times"
                  tooltip="Close"
                  onClick={() => setShowModal(false)}
                />
              </div>
            }
            body={
              <div style={{ margin: 15 }}>
                <ResponsiveContainer height={40 * data.length} width="100%">
                  <Bar
                    pretty
                    data={data}
                    height={40 * data.length}
                    width={windowWidth > 400 ? windowWidth - 200 : 200}
                    xdata={{ label: config.xLabel, key: config.x }}
                    ydata={{ label: config.yLabel, key: config.y }}
                    percent={config.unit === 'percent'}
                    layout={layout}
                    colors={colors}
                    tooltip={(props) => <CustomTooltip config={config} {...props} />}
                    margin={{ bottom: 5 }}
                  />
                </ResponsiveContainer>
              </div>
            }
          />
        )}
      </Card>
    </Column>
  )
}

const CustomTooltip = ({
  active,
  payload,
  config,
}: TooltipProps & {
  config: any
}) => {
  if (!active || !payload) {
    return null
  }

  const d = payload[0].payload
  const x = d[config.x]
  const y = d[config.y]

  if (config.tooltip) {
    return <ChartTooltip label={config.tooltip({ x, y })} />
  }

  const currency = config.unit === '$' ? '$' : ''
  const unit = config.unit && config.unit !== '$' && (config.unit === '%' ? '%' : ` ${config.unit}`)

  return (
    <ChartTooltip
      label={y}
      value={
        <span>
          {currency}
          <PrettyNumber value={x} />
          {unit}
        </span>
      }
    />
  )
}

const formatValue = (columnType: string, value: string) =>
  ['int', 'decimal', 'percent'].indexOf(columnType) >= 0 ? +value : value

const formatChartData = ({
  columns,
  columnTypes,
  values,
  conf,
  horizontal,
  colors,
}: {
  columns: string[] | string
  columnTypes: string[] | string
  values: string[][]
  conf: any
  horizontal: boolean
  colors: any
}) => {
  const data = values.map((entry: any, index: number) => {
    const obj: any = {}
    entry.forEach((value: any, i: any) => {
      switch (columns[i]) {
        case 'id':
        case 'Id':
          obj.id = value
          if (colors) obj.color = colors[value]
          break
        case conf.x:
          obj[conf.x] = formatValue(columnTypes[i], value)
          break
        case conf.y:
          obj[conf.y] = formatValue(columnTypes[i], value)
          break
      }
    })
    return obj
  })

  return horizontal
    ? data
    : data.sort((d1: any, d2: any) => {
        return d2[conf.x] - d1[conf.x]
      })
}
